/**
 * Social Proof Section Component
 * Customer reviews, testimonials, and press mentions
 */

'use client';

import { useState } from 'react';
import styled from '@emotion/styled';
import { Rating, useTheme, ComponentSize } from '@template-project/afrodita-components';
import { useTranslation } from '@/lib/i18n';
import { LazySection } from '@/components/atoms/LazySection';

interface Testimonial {
  id: string;
  name: string;
  rating: number;
  content: string;
  isVerified: boolean;
}

interface ProductCategory {
  id: string;
  name: string;
}

const PRODUCT_CATEGORIES: ProductCategory[] = [
  { id: 'wireless-bra', name: 'Wireless Shaping Bra' },
  { id: 'cami', name: 'Cami' },
  { id: 'shaper-shorts', name: 'Shaper Shorts' },
  { id: 'shaping-leggings', name: 'Shaping Leggings' },
  { id: 'shaper-panty', name: 'Shaper Panty' },
];

const TESTIMONIALS: Record<string, Testimonial[]> = {
  'wireless-bra': [
    {
      id: '1',
      name: 'Alice R.',
      rating: 5,
      content: "It's very comfortable, it does not flatten your breasts like other brands do, the material is very breathable… the most perfect bra EVER!!",
      isVerified: true,
    },
    {
      id: '2',
      name: 'Harriet G.',
      rating: 5,
      content: "I wish I had gotten it earlier. I wore my new bra today ALL DAY and no back ache. Felt like I wasn't even in a bra. This is the first time in years I didn't have to take my bra off at work. I love my bras. Thank you.",
      isVerified: true,
    },
    {
      id: '3',
      name: 'Pamela D.',
      rating: 5,
      content: 'Loved this bra so much, I just bought 3 more. Will never wear any other bra.',
      isVerified: true,
    },
  ],
  'cami': [
    {
      id: '4',
      name: 'Sandra M.',
      rating: 5,
      content: 'This cami is amazing! It smooths everything out and is so comfortable I forget I have it on.',
      isVerified: true,
    },
    {
      id: '5',
      name: 'Jennifer L.',
      rating: 5,
      content: 'Perfect for layering. The shaping is subtle but effective. I own 5 now!',
      isVerified: true,
    },
    {
      id: '6',
      name: 'Maria K.',
      rating: 5,
      content: 'Finally found a cami that actually works. Great quality and super comfortable.',
      isVerified: true,
    },
  ],
  'shaper-shorts': [
    {
      id: '7',
      name: 'Lisa T.',
      rating: 5,
      content: 'These shorts are life changing! They stay in place and actually smooth everything.',
      isVerified: true,
    },
    {
      id: '8',
      name: 'Rachel W.',
      rating: 5,
      content: 'So comfortable I wear them every day. The best shapewear I have ever owned.',
      isVerified: true,
    },
    {
      id: '9',
      name: 'Angela P.',
      rating: 5,
      content: 'No rolling, no bunching. Finally shapewear that actually works!',
      isVerified: true,
    },
  ],
  'shaping-leggings': [
    {
      id: '10',
      name: 'Diana S.',
      rating: 5,
      content: 'These leggings are incredible. They shape and smooth without being uncomfortable.',
      isVerified: true,
    },
    {
      id: '11',
      name: 'Karen B.',
      rating: 5,
      content: 'I can wear these all day at work. So flattering and comfortable!',
      isVerified: true,
    },
    {
      id: '12',
      name: 'Michelle R.',
      rating: 5,
      content: 'Best leggings ever! They hold everything in place perfectly.',
      isVerified: true,
    },
  ],
  'shaper-panty': [
    {
      id: '13',
      name: 'Christina H.',
      rating: 5,
      content: 'These panties are amazing! No VPL and they actually stay comfortable all day.',
      isVerified: true,
    },
    {
      id: '14',
      name: 'Stephanie N.',
      rating: 5,
      content: 'Finally underwear that smooths without digging in. Love them!',
      isVerified: true,
    },
    {
      id: '15',
      name: 'Amanda C.',
      rating: 5,
      content: 'So comfortable and effective. I have replaced all my underwear with these.',
      isVerified: true,
    },
  ],
};

const Section = styled.section`
  padding: ${({ theme }) => theme.space['3xl']} ${({ theme }) => theme.space.md};
  background-color: ${({ theme }) => theme.colors.background.primary};

  @media (min-width: ${({ theme }) => theme.mediaQueries.xl}) {
    padding: ${({ theme }) => theme.space['3xl']} ${({ theme }) => theme.space.xl};
  }
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const RatingSummary = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.space.sm};
  margin-bottom: ${({ theme }) => theme.space.md};
`;

const RatingText = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Headline = styled.h2`
  font-size: ${({ theme }) => theme.typography.variants.heading2.fontSize}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: center;
  margin: 0 0 ${({ theme }) => theme.space.xl};
`;

const CategoryPills = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${({ theme }) => theme.space.sm};
  margin-bottom: ${({ theme }) => theme.space.xl};
`;

interface PillProps {
  $active: boolean;
}

const Pill = styled.button<PillProps>`
  padding: ${({ theme }) => theme.space.sm} ${({ theme }) => theme.space.lg};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  border-radius: ${({ theme }) => theme.radius.full};
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${({ $active, theme }) => $active ? `
    background-color: ${theme.colors.shades[700].color};
    color: ${theme.colors.shades.white.color};
    border: 1px solid ${theme.colors.shades[700].color};
  ` : `
    background-color: ${theme.colors.background.primary};
    color: ${theme.colors.text.primary};
    border: 1px solid ${theme.colors.border.color};
    
    &:hover {
      border-color: ${theme.colors.border.highlight};
    }
  `}
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.space.lg};
  
  @media (min-width: ${({ theme }) => theme.mediaQueries.md}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const TestimonialCard = styled.div`
  padding: ${({ theme }) => theme.space.lg};
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  background-color: ${({ theme }) => theme.colors.background.primary};
`;

const TestimonialHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.space.sm};
  margin-bottom: ${({ theme }) => theme.space.sm};
`;

const ReviewerName = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const VerifiedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.xs};
  padding: ${({ theme }) => theme.space.xs} ${({ theme }) => theme.space.sm};
  font-size: ${({ theme }) => theme.fontSizes[0]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  color: ${({ theme }) => theme.colors.semantic.positive.color};
  background-color: rgba(47, 128, 106, 0.1);
  border-radius: ${({ theme }) => theme.radius.small};
  text-transform: uppercase;
`;

const CheckIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
  </svg>
);

const TestimonialRating = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.space.md};
`;

const TestimonialContent = styled.p`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: center;
  line-height: 1.6;
  margin: 0;
`;

const PressSection = styled.div`
  margin-top: ${({ theme }) => theme.space['3xl']};
  padding-top: ${({ theme }) => theme.space['2xl']};
  border-top: 1px solid ${({ theme }) => theme.colors.border.color};
`;

const PressTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin: 0 0 ${({ theme }) => theme.space.sm};
`;

const PressSubtitle = styled.p`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
  margin: 0 0 ${({ theme }) => theme.space.xl};
  
  strong {
    font-weight: ${({ theme }) => theme.typography.config.weight.bold};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const PressLogos = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.space.xl} ${({ theme }) => theme.space['2xl']};
  
  @media (min-width: ${({ theme }) => theme.mediaQueries.md}) {
    gap: ${({ theme }) => theme.space.xl} ${({ theme }) => theme.space['3xl']};
  }
`;

const PressLogo = styled.div`
  opacity: 0.6;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 1;
  }
  
  svg {
    height: 28px;
    width: auto;
    
    @media (min-width: ${({ theme }) => theme.mediaQueries.md}) {
      height: 36px;
    }
  }
`;

// Press logo SVGs (simplified representations)
const GoodMorningAmericaLogo = () => (
  <svg viewBox="0 0 200 40" fill="#292929">
    <text x="0" y="30" fontFamily="serif" fontWeight="bold" fontSize="14">GOOD MORNING</text>
    <text x="0" y="38" fontFamily="serif" fontWeight="bold" fontSize="10">AMERICA</text>
  </svg>
);

const ForbesLogo = () => (
  <svg viewBox="0 0 120 40" fill="#292929">
    <text x="0" y="32" fontFamily="serif" fontWeight="bold" fontSize="32">Forbes</text>
  </svg>
);

const CNNLogo = () => (
  <svg viewBox="0 0 80 40" fill="#292929">
    <text x="0" y="32" fontFamily="sans-serif" fontWeight="bold" fontSize="32">CNN</text>
  </svg>
);

const NBCLogo = () => (
  <svg viewBox="0 0 60 40" fill="#292929">
    <text x="0" y="32" fontFamily="sans-serif" fontWeight="bold" fontSize="24">NBC</text>
  </svg>
);

const QVCLogo = () => (
  <svg viewBox="0 0 80 40" fill="#292929">
    <rect x="0" y="8" width="24" height="24" rx="2" fill="none" stroke="#292929" strokeWidth="2"/>
    <text x="4" y="26" fontFamily="sans-serif" fontWeight="bold" fontSize="14">Q</text>
    <text x="30" y="28" fontFamily="sans-serif" fontSize="20">QVC</text>
  </svg>
);

const NYTimesLogo = () => (
  <svg viewBox="0 0 120 50" fill="#292929">
    <text x="0" y="20" fontFamily="'Old English Text MT', serif" fontSize="16">The</text>
    <text x="0" y="38" fontFamily="'Old English Text MT', serif" fontWeight="bold" fontSize="20">New York</text>
    <text x="0" y="48" fontFamily="'Old English Text MT', serif" fontWeight="bold" fontSize="16">Times</text>
  </svg>
);

export function SocialProof() {
  const theme = useTheme();
  const [activeCategory, setActiveCategory] = useState('wireless-bra');
  const { t } = useTranslation();

  const currentTestimonials = TESTIMONIALS[activeCategory] || TESTIMONIALS['wireless-bra'];

  return (
    <LazySection minHeight="700px" ariaLabel="Social proof section loading">
    <Section theme={theme}>
      <Container>
        <RatingSummary theme={theme}>
          <RatingText theme={theme}>4.5/5</RatingText>
          <Rating rating={4.5} size={ComponentSize.Small} />
          <RatingText theme={theme}>{t('home.socialProofReviews')}</RatingText>
        </RatingSummary>
        
        <Headline theme={theme}>{t('home.socialProofHeadline')}</Headline>
        
        <CategoryPills theme={theme}>
          {PRODUCT_CATEGORIES.map((category) => (
            <Pill
              key={category.id}
              theme={theme}
              $active={activeCategory === category.id}
              onClick={() => setActiveCategory(category.id)}
            >
              {category.name}
            </Pill>
          ))}
        </CategoryPills>
        
        <TestimonialsGrid theme={theme}>
          {currentTestimonials.map((testimonial) => (
            <TestimonialCard key={testimonial.id} theme={theme}>
              <TestimonialHeader theme={theme}>
                <ReviewerName theme={theme}>{testimonial.name}</ReviewerName>
                {testimonial.isVerified && (
                  <VerifiedBadge theme={theme}>
                    <CheckIcon /> Verified Buyer
                  </VerifiedBadge>
                )}
              </TestimonialHeader>
              <TestimonialRating theme={theme}>
                <Rating rating={testimonial.rating} size={ComponentSize.Small} />
              </TestimonialRating>
              <TestimonialContent theme={theme}>
                {testimonial.content}
              </TestimonialContent>
            </TestimonialCard>
          ))}
        </TestimonialsGrid>
        
        <PressSection theme={theme}>
          <PressTitle theme={theme}>IN THE PRESS</PressTitle>
          <PressSubtitle theme={theme}>
            Our <strong>5-star</strong> styles have been cheered all over the world.
          </PressSubtitle>
          
          <PressLogos theme={theme}>
            <PressLogo><GoodMorningAmericaLogo /></PressLogo>
            <PressLogo><ForbesLogo /></PressLogo>
            <PressLogo><CNNLogo /></PressLogo>
            <PressLogo><NBCLogo /></PressLogo>
            <PressLogo><QVCLogo /></PressLogo>
            <PressLogo><NYTimesLogo /></PressLogo>
          </PressLogos>
        </PressSection>
      </Container>
    </Section>
    </LazySection>
  );
}
